/**
 * When rotating, updates "anchor" object rotation (camera and surrounding objects are static)
 */

import {Utils} from "./Utils";
import {App3D} from './App3D';

import * as THREE from "three";

export class Navigator {
	protected theta: number = null;
	protected distance: number = null;
	protected dist_range: any;
	protected th_range: any;
	protected phi_range: any;
	protected acceleration: any;
	protected acceleration_y: number;
	protected acceleration_w: any;
	protected acceleration_p: number;
	protected phi: number = null;
	protected camera: THREE.Camera = null;
	protected center: THREE.Vector3 = null;
	protected anchor: THREE.Object3D = null;
	protected zooming: any = null;
	protected panning: any = null;
	protected pan_speed: number;
	protected tilting: any = null;
	protected mv_origin: any = null;
	protected ob_static = [];
	protected manual_movement: boolean = false;

	protected _$eActivate: JQuery;	// where movement can start
	protected _$eMove: JQuery;	// catch movement events if movement already started
	protected _$eStop: JQuery;	// catch movement stop events

	protected rotation_offset: number = 0;
	protected autorotate:boolean = true;

	protected _onChange: ((navigator:Navigator) => void)[] = [];
	protected $: JQueryStatic;

	constructor(
			options: {},
			_$: JQueryStatic = null,
			app3D:App3D = null) {

		this.$ = _$ ? _$ : $; // use provided instance if set, global $ otherwise

		let o = new Utils.Options(options);
		// this.r = o.get('distance', 2);
		this.setConstraints(o);

		this.acceleration = o.get('acceleration', 0.5); // degrees per pixel
		this.acceleration_y = this.acceleration * o.get('acceleration_y', 1.0);
		this.acceleration_w = o.get('acceleration_zoom', 1); // units per "scroll"
		this.acceleration_p = this.acceleration_w * o.get('acceleration_pinch', 1); // units per "pinch scale"

		this.autorotate = o.get('autorotate', true);

		let eActivateFallback = null;
		if (app3D){
			this.camera = app3D.camera;
			this.center = app3D.center;
			this.anchor = app3D.anchor;
			eActivateFallback = app3D.view;
		}

		this.pan_speed = o.get('pan_speed', 0.8);

		// Movement controll
		// this.dist_quotient = Math.sqrt(this.azimuth * this.azimuth + this.elevation * this.elevation);
		this._$eActivate = this.$(o.get('container', eActivateFallback));
		this._$eMove = this.$('body');
		this._$eStop = this._$eMove;
		setTimeout(() => {
			this.attachEvents();
		}, 10);
	}

	/**
	 * Current view elevation (range from -1 (bottom) to 1 (top)).
	 * @return {number}
	 */
	get elevation():number{
		return (90 - this.theta) / 90;
	}

	/**
	 * Current view rotation (range from -1 (bottom) to 1 (top)).
	 * @return {number}
	 */
	get rotation():number{
		return this.phi;
	}

	/**
	 * Registers onchange handler.
	 * @param {() => void} callback
	 */
	onChange(callback: (navigator:Navigator) => void){
		this._onChange.push(callback);
	}

	protected hasChanged(){
		for (let cb of this._onChange)
			cb(this);
	}

	protected attachEvents(){
		if (typeof Hammer === 'function') {
			// Hammer.defaults.behavior.touchAction = 'pan-y';
			let hammer = new Hammer(this._$eActivate[0], {});
			hammer.get('pinch').set({
				enable: true
			});
			hammer.get('pan').set({
				direction: Hammer.DIRECTION_HORIZONTAL,
				threshold: 0
			});
			hammer.on('panstart', (e) => {
				this.eStart();
				return false;
			});
			hammer.on('panmove', (e) => {
				this.eMove(-e.deltaX, -e.deltaY);
				return true;
			});
			hammer.on('panend', (e) => {
				this.eEnd(-e.deltaX, -e.deltaY);
			});
			hammer.on('pinch', (e) => {
				let scale = (e.scale > 1)
					? (e.scale - 1)
					: -(1 - e.scale);
				this.zoom(scale * this.acceleration_p);
			});
		}
		else {
			this._$eActivate.mousedown((e) => {
				this.eStart(e.clientX, e.clientY);
			});
			this._$eMove.mousemove((e) => {
				if (this.mv_origin)
					this.eMove(this.mv_origin.ps - e.clientX, this.mv_origin.ts - e.clientY);

			});
			this._$eStop.mouseup((e) => {
				if (this.mv_origin)
					this.eEnd(this.mv_origin.ps - e.clientX, this.mv_origin.ts - e.clientY);
			});
		}
		if (typeof this._$eActivate.mousewheel !== 'function')
			console.warn('jQuery mousewheel not initialized: scroll-zoom is disabled');
		else
			this._$eActivate.mousewheel((e: JQueryMouseEventObject): boolean => {
				let b = this.$('html');
				if ((e.deltaY > 0) && b.scrollTop())
					return true;
				return this.zoom(e.deltaY * (e.deltaFactor / 50));
			});
	}

	/**
	 * @hidden
	 * Activation element.
	 */
	get $eActivate():JQuery{
		return this._$eActivate;
	}

	protected eStart(ps: number = 0, ts: number = 0){
		this.stopAutoMovement();
		this.manual_movement = true;
		this.mv_origin = {pr: this.phi, ps: ps, tr: this.theta, ts: ts};
	}

	protected eMove(dx: number, dy: number){
		if (this.manual_movement) {
			this.stopAutoMovement(false);
			this.move({dx: dx, dy: dy});
		}
	}

	protected eEnd(dx: number, dy: number){
		if (this.manual_movement) {
			this.move({dx: dx, dy: dy});
			this.mv_origin = null;
			this.manual_movement = false;
			if (this.autorotate)
				setTimeout(() => {
					this.panStart();
				}, 2000);
		}
	}

	/**
	 * Move view by offset
	 * @param delta {dx: number, dy:number}
	 */
	protected move(delta) {
		if (!this.mv_origin)
			return false;

		let elevation:number = this.mv_origin.tr + delta.dy;
		let rotation:number = this.mv_origin.pr + delta.dx;
		// fix start position to match new elevation if elevation exceeds [min,max]
		this.setRotation(
			this.phi_range.limit(rotation),
			this.th_range.limit(elevation));
		return !(this.th_range.fit(rotation) && this.phi_range.fit(elevation));
	}

	/**
	 * Starts zooming.
	 * @param delta Zoom speed
	 */
	zoomStart(delta) {
		this.zoomStop();
		this.zooming = setInterval(() => {
			this.zoom(delta);
		}, 100);
	}

	/**
	 * Stops Zooming.
	 */
	zoomStop() {
		if (this.zooming) {
			clearInterval(this.zooming);
		}
	}

	/**
	 * Starts pan (rotate in time).
	 * @param delta Rotation speed
	 */
	panStart(delta:number = this.pan_speed) {
		this.panStop();
		if (!this.mv_origin)
			this.mv_origin = {pr: this.phi, ps: 0, tr: this.theta, ts: 0};
		let pos = 0;
		let r_speed = 0;
		this.$({r_speed: 0}).animate({r_speed: delta}, {
			duration: 1500,
			step: function () {
				r_speed = this.r_speed;
			}
		});
		this.panning = setInterval(() => {
			pos += r_speed;
			if (!this.phi_range.fit(pos))
				r_speed *= -1;
			this.move({
				dx: pos,
				dy: 0
			});
		}, 20);
	};

	/**
	 * Stops pan.
	 * @param hard Clear movement origin
	 */
	panStop(hard:boolean = false):void {
		if (this.panning)
			clearInterval(this.panning);
		if (hard)
			this.mv_origin = null;
	}

	/**
	 * Starts elevation change.
	 * @param delta
	 */
	tiltStart(delta:number) {
		this.tiltStop();
		if (!this.mv_origin)
			this.mv_origin = {pr: this.phi, ps: 0, tr: this.theta, ts: 0};
		let pos = 0;
		this.tilting = setInterval(() => {
			pos += delta;
			this.move({
				dx: 0,
				dy: pos
			});
		}, 20);
	}

	/**
	 * Stops elevation change.
	 * @param hard Clear movement origin
	 */
	tiltStop(hard:boolean = false):void {
		if (this.tilting)
			clearInterval(this.tilting);
		if (hard)
			this.mv_origin = null;
	}

	/**
	 * Stops auto-rotation
	 * @param hard
	 */
	stopAutoMovement(hard:boolean = false):void {
		this.panStop(hard);
		this.tiltStop(hard);
	}

	/**
	 * Zooms in or out
	 * @param delta
	 * @return Whether still zooming
	 */
	zoom(delta):boolean {
		let dOrg = this.distance;
		this.distance -= delta * this.acceleration_w;
		if (this.distance < this.dist_range.min)
			this.distance = this.dist_range.min;
		else if (this.distance > this.dist_range.max)
			this.distance = this.dist_range.max;
		this.updateCameraPosition();
		return dOrg == this.distance;
	};

	/**
	 * @hidden
	 */
	setAnchor(anchor):void {
		this.anchor = anchor;
		this.updateAnchorRotation();
	};

	/**
	 * @hidden
	 */
	setCamera(camera, center):void {
		this.camera = camera;
		this.center = center;
		this.updateCameraPosition();
	};

	/**
	 * Sets navigator constraints (and other behaviour)
	 * @param {Utils.Options} constraints
	 * @param {boolean} extend Whether to extend existing settings or init new one
	 */
	setConstraints(constraints:Utils.Options, extend:boolean = false){
		if (!extend || constraints.has('rotation_range'))
			this.phi_range = new Utils.Range(constraints.get('rotation_range', {min: -90, max: 90}));
		if (!extend || constraints.has('elevation_range'))
			this.th_range = new Utils.Range(constraints.get('elevation_range', {min: 5, max: 90}));
		if (!extend || constraints.has('distance_range'))
			this.dist_range = new Utils.Range(constraints.get('distance_range', {min: 5, max: 10}));
		if (!extend || constraints.has('autorotate')) {
			this.autorotate = <boolean>constraints.get('autorotate', false);
			if (this.autorotate)
				setTimeout(() => {
					this.panStart();
				}, 1000);
		}

		// convert intuitive range with zero on horizon (+ up, -down) to real one with zero on north pole (+ down)
		this.th_range = new Utils.Range(90-this.th_range.max, 90-this.th_range.min);
		this.phi = this.phi_range.limit((constraints.has('rotation') || (this.phi === null))
			? Number(constraints.get('rotation', 0))
			: this.phi);
		this.theta = this.th_range.limit((constraints.has('elevation') || (this.theta === null))
			? 90 - Number(constraints.get('elevation', 35))
			: this.theta);
		this.distance = this.dist_range.limit((constraints.has('distance') || (this.distance === null))
			? Number(constraints.get('distance', 10))
			: this.distance);
		this.updateView();
	}

	/**
	 * @param {number} rotation rotation in Deg
	 * @param {number} elevation camera elevation in Deg
	 */
	setRotation(rotation:number = null, elevation:number = null):void {
		if (rotation !== null)
			this.phi = rotation;
		if (elevation !== null)
			this.theta = elevation;
		this.updateView();
	};

	/**
	 * Sets rotation offset in degrees
	 * @param {number} offset
	 * @hidden
	 */
	setRotationOffset(offset:number){
		this.rotation_offset = offset;
		this.updateAnchorRotation();
	}

	protected updateView(){
		this.updateAnchorRotation(false);
		this.updateCameraPosition(false);
		this.hasChanged();
	}

	protected updateAnchorRotation(invokeChange: boolean = true):void {
		if (!this.anchor)
			return;

		if (!this.camera)
			this.anchor.rotation.x = (0 - this.theta) * Math.PI / 180;
		this.anchor.rotation.y = (-this.phi + this.rotation_offset) * Math.PI / 180;
		if (invokeChange)
			this.hasChanged();
	}

	protected updateCameraPosition(invokeChange: boolean = true):void {
		if (!this.camera)
			return;
		let theta = (90 - this.theta) * Math.PI / 180;
		this.camera.position.set(
			0,
			this.distance * Math.sin(theta),
			this.center.z - this.distance * Math.cos(theta));
		this.camera.lookAt(this.center);
		if (invokeChange)
			this.hasChanged();
	}

	/**
	 * @hidden
	 */
	dumpState(){
		console.log({
			rotation_offset: this.rotation_offset,
			rotation_range: this.phi_range,
			elevation_range: this.th_range,
			distance_range: this.dist_range,
			autorotate: this.autorotate,
			rotation: this.phi,
			elevation: this.theta,
			distance: this.distance
		})
	}
}



// WEBPACK FOOTER //
// ./src/script/app3D/Navigator.ts